// Drag files anywhere over the Submit page (DESIGN_SPEC §10.7 drop target, §11.2 dragging state).
//   useWindowFileDrop(onFiles) → { dragging, count }   window-level: only drags that carry files; the drop never
//                                                      navigates away; count = files in the drag, when the browser says
//   <DropOverlay active specimenNo? count? />          fixed overlay: "Drop to pin specimen 0015", or "Drop 3 files"
// Leaving the window has no reliable event, so the overlay closes once dragover stops arriving (350 ms).
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const carriesFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");

export function useWindowFileDrop(onFiles: (files: File[]) => void): { dragging: boolean; count: number } {
  const [dragging, setDragging] = useState(false);
  const [count, setCount] = useState(0);
  const latest = useRef(onFiles);
  useLayoutEffect(() => {
    latest.current = onFiles;
  });

  useEffect(() => {
    let timer = 0;
    const stop = () => {
      window.clearTimeout(timer);
      setDragging(false);
    };
    const over = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setCount(e.dataTransfer?.items ? Array.from(e.dataTransfer.items).filter((i) => i.kind === "file").length : 0);
      setDragging(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(stop, 350);
    };
    const drop = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      stop();
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) latest.current(files);
    };
    window.addEventListener("dragenter", over);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    window.addEventListener("dragend", stop);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("dragenter", over);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
      window.removeEventListener("dragend", stop);
    };
  }, []);

  return { dragging, count };
}

export function DropOverlay({ active, specimenNo, count = 0 }: { active: boolean; specimenNo?: string | null; count?: number }) {
  if (!active) return null;
  return createPortal(
    <div className="drop-overlay" aria-hidden="true">
      <p>{count > 1 ? <>Drop {count} files</> : <>Drop to pin {specimenNo ? <>specimen {specimenNo}</> : "the specimen"}</>}</p>
    </div>,
    document.body,
  );
}
